import {
    Avatar,
    Colors,
    DATE_FORMATS,
    DateInput,
    formatDate,
    Icon,
    Spinner,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseHandoutDate } from './handoutDate';
import { HandoutEvalPeriodModal } from './HandoutEvalPeriodModal';
import { CheckIcon, PenIcon } from '../PrepareMeetingIcons';

import type { EvaluationPeriodOption } from '../../Evaluation/types';
import type { User } from '@/types/directory/User';

import { useGetAllCompanyUsers, useGetUserById } from '@/api/directory';
import { companyAtom } from '@/atoms/company';
import { Locale } from '@/constants/Locale';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useDebounce } from '@/hooks/useDebounce';

const getManagerInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
};

interface HandoutHeaderProps {
    evaluationDate: string;
    evaluationStartDate: string;
    evaluationEndDate: string;
    managerName: string;
    currentPage: number;
    totalPages: number;
    onEvaluationDateChange: (value: string) => void;
    onManagerNameChange: (value: string) => void;
    /** The employee being evaluated; used to exclude them from manager search results. */
    employeeId?: string;
    /** Persisted manager override for the handout header. */
    onManagerNameSave?: (value: string) => void;
    /** Available evaluation periods for the modal */
    evaluationPeriods?: EvaluationPeriodOption[];
    /** Currently selected period ID (from parent state) */
    selectedPeriodId?: string;
    /** Callback when user saves a period in the modal */
    onEvaluationPeriodSave?: (startDate: string, endDate: string, periodId: string) => void;
}

export const HandoutHeader = ({
    evaluationDate,
    evaluationStartDate,
    evaluationEndDate,
    managerName,
    currentPage,
    totalPages,
    onEvaluationDateChange,
    onManagerNameChange,
    employeeId,
    onManagerNameSave,
    evaluationPeriods = [],
    selectedPeriodId = '',
    onEvaluationPeriodSave,
}: HandoutHeaderProps) => {
    const { t, i18n } = useTranslation();
    const { getManagerLabelCapitalized } = useCompanyInformation();
    const headerRef = useRef<HTMLDivElement>(null);
    const [editingField, setEditingField] = useState<'date' | 'period' | 'manager' | null>(null);
    const [isEvalPeriodModalOpen, setIsEvalPeriodModalOpen] = useState(false);
    const [managerSearch, setManagerSearch] = useState('');
    const debouncedManagerSearch = useDebounce(managerSearch, 400);
    const company = useAtomValue(companyAtom);
    const managers = company?.managers ?? [];
    const hasSearch = managerSearch.trim().length >= 2;
    const trimmedSearchLower = debouncedManagerSearch.trim().toLowerCase();
    const { data: allCompanyUsers = [], isLoading: isUsersLoading } = useGetAllCompanyUsers({
        enabled: hasSearch,
        isActiveOnly: true,
    });
    const { data: currentUser } = useGetUserById({ userId: 'me' });
    const currentUserId = currentUser?.id;

    const subordinateIds = useMemo(() => {
        if (!employeeId || allCompanyUsers.length === 0) {
            return new Set<string>();
        }

        const byManager = new Map<string, string[]>();
        (allCompanyUsers as User[]).forEach((user) => {
            if (!user.managerId) return;
            const list = byManager.get(user.managerId) ?? [];
            list.push(user.id);
            byManager.set(user.managerId, list);
        });

        const result = new Set<string>();
        const queue: string[] = [employeeId];

        while (queue.length > 0) {
            const current = queue.shift()!;
            const children = byManager.get(current);
            if (!children) continue;

            for (const childId of children) {
                if (!result.has(childId)) {
                    result.add(childId);
                    queue.push(childId);
                }
            }
        }

        result.delete(employeeId);
        return result;
    }, [employeeId, allCompanyUsers]);

    const filteredManagers = useMemo(() => {
        if (!company || !hasSearch) {
            return [];
        }

        const activeManagers = managers.filter((manager) => manager.isActive !== false);
        const withoutAssesseeAndSubordinates = activeManagers.filter((manager) => {
            if (employeeId && manager.id === employeeId) {
                return false;
            }
            if (currentUserId && manager.id === currentUserId) {
                return false;
            }
            return !subordinateIds.has(manager.id);
        });

        if (!trimmedSearchLower) {
            return withoutAssesseeAndSubordinates;
        }

        return withoutAssesseeAndSubordinates.filter((manager) =>
            manager.name.toLowerCase().includes(trimmedSearchLower),
        );
    }, [
        company,
        managers,
        employeeId,
        currentUserId,
        hasSearch,
        trimmedSearchLower,
        subordinateIds,
    ]);

    const isManagerLoading = !company || isUsersLoading;
    const formatHandoutDate = (value: string): string => {
        const parsedDate = parseHandoutDate(value);
        if (!parsedDate) {
            return value;
        }

        return formatDate({
            date: parsedDate,
            language: i18n.language,
            format: DATE_FORMATS.DISPLAY_DATE,
        });
    };
    const formattedEvaluationDate = formatHandoutDate(evaluationDate);
    const formattedStartDate = formatHandoutDate(evaluationStartDate);
    const formattedEndDate = formatHandoutDate(evaluationEndDate);

    // Keep the picked calendar day but carry the current time-of-day
    // so the payload matches the Evaluation page.
    const buildEvaluationDateIso = (value: Date): string => {
        const now = new Date();
        const picked = new Date(value);
        picked.setHours(now.getHours(), now.getMinutes(), 0, 0);
        return picked.toISOString();
    };

    const handlePeriodSave = (periodId: string, startDate: string, endDate: string) => {
        onEvaluationPeriodSave?.(startDate, endDate, periodId);
    };

    useEffect(() => {
        if (editingField === 'manager') {
            setManagerSearch(managerName);
        }
    }, [editingField, managerName]);

    useEffect(() => {
        if (!editingField) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (!headerRef.current) {
                return;
            }

            const targetPath = event.composedPath();
            const activeField = headerRef.current.querySelector(
                `[data-editing-field="${editingField}"]`,
            );

            if (activeField && targetPath.includes(activeField)) {
                return;
            }

            // The DateInput popover may be portalled outside the field wrapper;
            // a click inside it must not exit edit mode.
            if (
                targetPath.some(
                    (node) =>
                        node instanceof HTMLElement &&
                        node.dataset.testid === 'handout-evaluation-date-input-popover',
                )
            ) {
                return;
            }

            if (editingField === 'manager') {
                setManagerSearch('');
            }

            setEditingField(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editingField]);

    const showManagerResults = hasSearch && !isManagerLoading && filteredManagers.length > 0;
    const showManagerEmptyState = hasSearch && !isManagerLoading && filteredManagers.length === 0;

    return (
        <div
            className={
                '!relative z-10 mt-[112px] !overflow-visible bg-gradient-to-r from-[#098b75] to-[#0dd3bc] !p-[16px] text-white sm:!p-[24px]'
            }
            ref={headerRef}
        >
            <div
                className={
                    'absolute top-0 right-0 -mt-20 -mr-20 h-40 w-40 rounded-full bg-white/10'
                }
            ></div>
            <div
                className={
                    'absolute bottom-0 left-0 -mb-16 -ml-16 h-32 w-32 rounded-full bg-white/10'
                }
            ></div>
            <div className={'flex flex-col sm:flex-row sm:items-center sm:justify-between'}>
                <div className={'flex-1'}>
                    <h1 className={'text-heading mb-[4px] font-bold'}>
                        {t('postReviewPrepareMeeting.handout.title')}
                    </h1>
                    <div className={'space-y-[4px]'}>
                        <div
                            className={'group/field flex items-center gap-2 !text-base !text-white'}
                            data-editing-field={'date'}
                        >
                            {editingField === 'date' ? (
                                <DateInput
                                    dataTestId={'handout-evaluation-date'}
                                    placeholder={t('common.selectDate')}
                                    value={parseHandoutDate(evaluationDate)}
                                    onChange={(value) => {
                                        if (!value) {
                                            return;
                                        }

                                        onEvaluationDateChange(buildEvaluationDateIso(value));
                                        setEditingField(null);
                                    }}
                                    lang={i18n.language === Locale.EN ? 'en' : 'fr'}
                                    showTime={false}
                                    clearable={false}
                                    className={'!text-base'}
                                    inputClassName={
                                        '!rounded-md !border !border-white/50 !bg-white/20 !text-white transition-colors hover:!bg-white/30 focus:!border-white/50 focus:!bg-white/30 focus:!ring-0 [&_span]:!text-white [&_svg]:!text-white [&_svg_*]:!stroke-white'
                                    }
                                    variant={'violet'}
                                />
                            ) : (
                                <p className={'text-base !text-white'}>
                                    {t('postReviewPrepareMeeting.handout.evaluationDate', {
                                        date: formattedEvaluationDate,
                                    })}
                                </p>
                            )}
                            <button
                                className={`rounded-md bg-white/10 p-[4px] opacity-0 transition-opacity group-hover/field:opacity-100 focus-visible:opacity-100 ${editingField === 'date' ? 'opacity-100' : ''}`}
                                type={'button'}
                                data-pdf-hide={'true'}
                                onClick={() =>
                                    setEditingField((current) =>
                                        current === 'date' ? null : 'date',
                                    )
                                }
                            >
                                <PenIcon className={'h-[14px] w-[14px] text-white'} />
                            </button>
                        </div>
                        <div
                            className={'group/field flex items-center gap-2'}
                            data-editing-field={'period'}
                        >
                            <p>
                                {formattedStartDate && formattedEndDate
                                    ? t('postReviewPrepareMeeting.handout.evaluationPeriod', {
                                          startDate: formattedStartDate,
                                          endDate: formattedEndDate,
                                      })
                                    : t('managerAssessmentEvaluation.selectPeriod')}
                            </p>
                            <button
                                className={`rounded-md bg-white/10 p-[4px] opacity-0 transition-opacity group-hover/field:opacity-100 focus-visible:opacity-100`}
                                type={'button'}
                                data-pdf-hide={'true'}
                                onClick={() => setIsEvalPeriodModalOpen(true)}
                            >
                                <PenIcon className={'h-[14px] w-[14px] text-white'} />
                            </button>
                        </div>
                        <div
                            className={'group/field flex items-center gap-2'}
                            data-editing-field={'manager'}
                        >
                            {editingField === 'manager' ? (
                                <div
                                    className={'relative'}
                                    data-pdf-hide={'true'}
                                >
                                    <div
                                        className={
                                            'flex w-[280px] items-center gap-[8px] rounded-[8px] border border-white/50 bg-white/20 px-[12px] py-[8px] transition-colors hover:bg-white/30'
                                        }
                                    >
                                        <Icon
                                            name={'search-search-normal-1'}
                                            color={Colors.WHITE}
                                            size={16}
                                        />
                                        <input
                                            className={
                                                'w-full bg-transparent text-sm leading-normal text-white outline-none placeholder:text-white/70'
                                            }
                                            value={managerSearch}
                                            onChange={(event) =>
                                                setManagerSearch(event.target.value)
                                            }
                                            placeholder={t('managerAssessmentSetup.searchEmployee')}
                                            autoFocus={true}
                                        />
                                        {isManagerLoading && <Spinner size={'xs'} />}
                                    </div>

                                    <div
                                        className={
                                            'absolute top-full left-0 z-50 mt-[8px] w-[280px] overflow-hidden rounded-[8px] border border-gray-200 bg-white text-gray-800 shadow-2xl'
                                        }
                                    >
                                        <div className={'max-h-[240px] overflow-y-auto'}>
                                            {showManagerResults &&
                                                filteredManagers.map((manager) => {
                                                    const isSelected = manager.name === managerName;

                                                    return (
                                                        <button
                                                            key={manager.id}
                                                            type={'button'}
                                                            data-pdf-hide={'true'}
                                                            className={`hover:bg-primary/5 flex w-full items-center gap-[12px] px-[16px] py-[12px] text-left transition-colors ${
                                                                isSelected ? 'bg-primary/10' : ''
                                                            }`}
                                                            onClick={() => {
                                                                const nextName = manager.name;
                                                                onManagerNameChange(nextName);
                                                                onManagerNameSave?.(nextName);
                                                                setManagerSearch('');
                                                                setEditingField(null);
                                                            }}
                                                        >
                                                            <Avatar
                                                                initials={getManagerInitials(
                                                                    manager.name,
                                                                )}
                                                                bgColor={'primary'}
                                                                size={'2xs'}
                                                                borderColor={Colors.BASE_100}
                                                                borderSize={'ring-2'}
                                                                alt={manager.name}
                                                                className={'flex-shrink-0'}
                                                            />
                                                            <span className={'min-w-0 flex-1'}>
                                                                <span
                                                                    className={
                                                                        'flex items-center gap-[8px]'
                                                                    }
                                                                >
                                                                    <span
                                                                        className={
                                                                            'text-navy truncate text-sm leading-normal font-semibold'
                                                                        }
                                                                    >
                                                                        {manager.name}
                                                                    </span>
                                                                    {isSelected && (
                                                                        <CheckIcon
                                                                            className={
                                                                                'text-primary h-[16px] w-[16px] flex-shrink-0'
                                                                            }
                                                                            strokeWidth={2}
                                                                        />
                                                                    )}
                                                                </span>
                                                            </span>
                                                        </button>
                                                    );
                                                })}

                                            {showManagerEmptyState && (
                                                <div
                                                    className={
                                                        'p-[16px] text-center text-xs leading-normal text-gray-500'
                                                    }
                                                >
                                                    {t('managerAssessmentSetup.noEmployeesFound')}
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className={
                                                'flex items-center justify-end gap-[8px] border-t border-gray-200 bg-gray-50 p-[12px]'
                                            }
                                        >
                                            <button
                                                type={'button'}
                                                data-pdf-hide={'true'}
                                                onClick={() => {
                                                    setManagerSearch('');
                                                    setEditingField(null);
                                                }}
                                                className={
                                                    'rounded-[8px] border border-gray-200 bg-white px-[12px] py-[6px] text-xs leading-normal font-medium text-gray-900 transition-colors hover:bg-gray-50'
                                                }
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p>
                                    {t('postReviewPrepareMeeting.handout.manager', {
                                        name: managerName,
                                        managerLabel: getManagerLabelCapitalized(),
                                    })}
                                </p>
                            )}
                            <button
                                className={`rounded-md bg-white/10 p-[4px] opacity-0 transition-opacity group-hover/field:opacity-100 focus-visible:opacity-100 ${editingField === 'manager' ? 'opacity-100' : ''}`}
                                type={'button'}
                                data-pdf-hide={'true'}
                                onClick={() =>
                                    setEditingField((current) =>
                                        current === 'manager' ? null : 'manager',
                                    )
                                }
                            >
                                <PenIcon className={'h-[14px] w-[14px] text-white'} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className={'mt-4 flex flex-col items-end gap-2 text-right sm:mt-0'}>
                    <p className={'!text-base font-semibold whitespace-nowrap'}>
                        {t('postReviewPrepareMeeting.handout.pageNumber', {
                            current: currentPage,
                            total: totalPages,
                        })}
                    </p>
                </div>
            </div>

            {/* Evaluation Period Modal */}
            <HandoutEvalPeriodModal
                open={isEvalPeriodModalOpen}
                onOpenChange={setIsEvalPeriodModalOpen}
                selectedPeriodId={selectedPeriodId}
                onPeriodSave={handlePeriodSave}
                customFromDate={parseHandoutDate(evaluationStartDate)}
                customToDate={parseHandoutDate(evaluationEndDate)}
                periods={evaluationPeriods}
            />
        </div>
    );
};
